import { useRef, useState } from "react";
import { createUserWithEmailAndPassword, sendEmailVerification, signOut } from "firebase/auth";
import { auth } from "../../firebase/auth";
import { db } from "../../firebase/firestore";
import {
    doc,
    serverTimestamp,
    setDoc,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import "../../Css/Login.css";
import { retryAsync } from "../../utils/errorHandler";
import { normalizeDeliveryArea, SERVICEABLE_AREAS } from "../../utils/helpers";
import {
    checkFirebaseAuthReachability,
    getFirebaseAuthTroubleshootingHint,
} from "../../utils/firebaseConnection";
import logo from "../../assets/logo.png";
import heroImage from "../../assets/hero.png";
export default function Register() {

    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        password: "",
        confirmPassword: "",
        address: "",
        location: ""
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [successMessage, setSuccessMessage] = useState("");

    const registerCardRef = useRef(null);
    const nameInputRef = useRef(null);

    const navigate = useNavigate();

    const getFriendlyRegisterError = (code) => {
        switch (code) {
            case "auth/email-already-in-use":
                return "This email is already registered. Redirecting to login...";
            case "auth/invalid-email":
                return "Please enter a valid email address";
            case "auth/weak-password":
                return "Password is too weak. Use at least 6 characters";
            case "auth/network-request-failed":
                return "Network issue detected. Please try again";
            case "auth/operation-not-allowed":
                return "Email/password sign-up is disabled in Firebase Auth settings";
            case "auth/invalid-api-key":
                return "Firebase API key is invalid. Check deployment environment config";
            case "auth/configuration-not-found":
                return "Firebase auth configuration is missing for this project";
            default:
                return "Could not create account. Please try again";
        }
    };

    const isStrongPassword = (pwd) => {
        const hasMinLength = pwd.length >= 8;
        const hasUpper = /[A-Z]/.test(pwd);
        const hasLower = /[a-z]/.test(pwd);
        const hasNumber = /\d/.test(pwd);
        return hasMinLength && hasUpper && hasLower && hasNumber;
    };

    const goToLoginSoon = () => {
        setTimeout(() => {
            navigate("/login", { replace: true });
        }, 1400);
    };

    const handleChange = (e) => {
        setForm({
            ...form,
            [e.target.name]: e.target.value
        });
    };

    const handleRegister = async () => {

        setError("");
        setSuccessMessage("");

        const normalized = {
            name: form.name.trim(),
            email: form.email.trim().toLowerCase(),
            phone: form.phone.trim(),
            password: form.password,
            confirmPassword: form.confirmPassword,
            address: form.address.trim(),
            location: form.location,
        };

        if (!normalized.name || !normalized.email || !normalized.password) {
            setError("Please fill all required fields");
            return;
        }

        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized.email)) {
            setError("Please enter a valid email address");
            return;
        }

        if (!SERVICEABLE_AREAS.includes(normalized.location)) {
            setError("We are coming to your area soon. Please choose a listed service area");
            return;
        }

        if (normalized.password !== normalized.confirmPassword) {
            setError("Passwords do not match");
            return;
        }

        if (!isStrongPassword(normalized.password)) {
            setError("Password must be 8+ chars with uppercase, lowercase, and a number");
            return;
        }

        if (normalized.phone && !/^\d{10}$/.test(normalized.phone)) {
            setError("Enter a valid 10-digit phone number");
            return;
        }

        if (typeof navigator !== "undefined" && !navigator.onLine) {
            setError("You appear to be offline. Please connect to the internet and try again");
            return;
        }

        setLoading(true);

        try {
            console.log("📝 Starting registration for:", normalized.email);
            
            // ✅ STEP 1: Create Firebase Auth
            const res = await retryAsync(
                () => createUserWithEmailAndPassword(auth, normalized.email, normalized.password),
                2,
                350
            );
            const user = res.user;
            console.log("✅ Auth user created:", user.uid);

            // ✅ STEP 2: Save to Firestore
            const userData = {
                name: normalized.name,
                email: normalized.email,
                phone: normalized.phone,
                address: normalized.address,
                location: normalizeDeliveryArea(normalized.location),
                role: "user",
                createdAt: serverTimestamp(),
                online: true,
                blocked: false,
                orderCount: 0
            };

            try {
                await setDoc(doc(db, "users", user.uid), userData);
                console.log("✅ Firestore user saved:", user.uid);
            } catch (dbError) {
                console.error("❌ Database save failed:", dbError.code, dbError.message);
                await signOut(auth);
                setError("Failed to save user data. Please try again.");
                setLoading(false);
                return;
            }

            // ✅ STEP 3: Send verification email (don't block on failure)
            try {
                await sendEmailVerification(user);
                console.log("✅ Verification email sent");
            } catch (emailError) {
                console.warn("⚠️ Verification email failed:", emailError.code);
                // Don't fail registration if email fails
            }

            // ✅ STEP 4: Sign out and redirect
            await signOut(auth);
            setSuccessMessage("✓ Account created! Please verify your email and login.");
            setTimeout(() => {
                navigate("/login", { replace: true });
            }, 2000);

        } catch (err) {
            console.error("❌ Registration error:", err.code, err.message);

            if (err.code === "auth/network-request-failed") {
                const networkCheck = await checkFirebaseAuthReachability();
                if (!networkCheck.ok) {
                    setError(`Auth service unreachable (${networkCheck.reason}). ${getFirebaseAuthTroubleshootingHint()}`);
                } else {
                    setError("Your internet is working, but Firebase auth requests are unstable. Please retry in a moment.");
                }
            } else {
                const msg = getFriendlyRegisterError(err.code);
                setError(msg);
            }

            if (err.code === "auth/email-already-in-use") {
                goToLoginSoon();
            }
            setLoading(false);
        }
    };

    return (
        <div className="login-container">

            <div className="bg-glow"></div>
            <div className="bg-glow2"></div>

            <div className="mobile-brand">
                <h1>Shreeji Surti Swad</h1>
            </div>

            <div className="mobile-logo">
                <div className="logo-icon">
                    <img src={logo} alt="logo" />
                </div>
            </div>

            <div className="login-left">

                <div className="login-card" ref={registerCardRef}>

                    <h2>Create Account 🚀</h2>
                    <p>Join and start ordering delicious food</p>

                    {/* ERROR UI */}
                    {error && <div className="error-box">⚠ {error}</div>}
                    {successMessage && <div className="info-box">{successMessage}</div>}

                    <form
                        className="auth-form"
                        onSubmit={(e) => {
                            e.preventDefault();
                            handleRegister();
                        }}
                    >
                        <div className="input-box">
                            <input
                                ref={nameInputRef}
                                name="name"
                                value={form.name}
                                onChange={handleChange}
                                placeholder=" "
                                autoComplete="name"
                                required
                            />
                            <label>Full Name</label>
                        </div>

                        <div className="input-box">
                            <input
                                type="email"
                                name="email"
                                value={form.email}
                                onChange={handleChange}
                                placeholder=" "
                                autoComplete="email"
                                required
                            />
                            <label>Email</label>
                        </div>

                        <div className="input-box">
                            <input
                                name="phone"
                                value={form.phone}
                                onChange={handleChange}
                                placeholder=" "
                                autoComplete="tel"
                                inputMode="numeric"
                            />
                            <label>Phone (+91)</label>
                        </div>

                        {/* 🔥 LOCATION SELECT */}
                        <div className="input-box">
                            <span className="select-label">Service Area</span>
                            <select
                                name="location"
                                value={form.location}
                                onChange={handleChange}
                                className="select-box"
                            >
                                <option value="">Select Area</option>
                                {SERVICEABLE_AREAS.map((area) => (
                                    <option key={area} value={area}>
                                        {area}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="input-box">
                            <input
                                type="password"
                                name="password"
                                value={form.password}
                                onChange={handleChange}
                                placeholder=" "
                                autoComplete="new-password"
                                required
                            />
                            <label>Password</label>
                        </div>

                        <div className="input-box">
                            <input
                                type="password"
                                name="confirmPassword"
                                value={form.confirmPassword}
                                onChange={handleChange}
                                placeholder=" "
                                autoComplete="new-password"
                                required
                            />
                            <label>Confirm Password</label>
                        </div>

                        <div className="input-box">
                            <input
                                name="address"
                                value={form.address}
                                onChange={handleChange}
                                placeholder=" "
                                autoComplete="street-address"
                            />
                            <label>Address</label>
                        </div>

                        <button
                            type="submit"
                            className={`shine-btn ${loading ? "loading" : ""}`}
                            disabled={loading}
                        >
                            {loading ? "Creating..." : "Register →"}
                        </button>

                        <button
                            type="button"
                            className="create"
                            onClick={() => navigate("/login", { replace: true })}
                        >
                            Already have account? Login
                        </button>
                    </form>

                </div>

            </div>

            <div className="login-right" style={{ "--hero-image": `url(${heroImage})` }}>

                <div className="badge badge1">🎉 Welcome Offer</div>
                <div className="badge badge2">🍽️ Best Taste</div>

                <div className="overlay-text">
                    <h2>Join Surti Family</h2>
                    <p>Create account & unlock offers</p>
                </div>

            </div>

        </div>
    );
}